/**
 * Barcode Scan Timing Logger
 *
 * Lightweight timing instrumentation for the container barcode-scan step,
 * built on the same CloudWatch (KatalLogger) pipeline as captureTimingLogger,
 * so it is automatically enabled on deployed stacks (NODE_ENV=production +
 * injected LoggerUrl) and console-only on local dev.
 *
 * What it measures:
 *  - scanMs: user-perceived time to successfully scan / enter a container
 *    barcode, from when the scan screen becomes ready until a valid barcode is
 *    confirmed. Intentionally includes camera warm-up and user aiming time.
 *
 * Dimensions:
 *  - entryMethod: "camera" (read via the camera scanner) vs "manual" (typed in
 *    via the manual-entry fallback).
 *
 * ============================================
 * CLOUDWATCH METRICS (namespace: SafetyGenie/ClientCapture)
 * ============================================
 * Published via EMF, dimension entryMethod. Value is raw per-scan; prefer
 * CloudWatch statistics (Average / p50 / p90) for aggregates.
 *  - BarcodeScanTimeMs                        user-perceived time of the final
 *                                             (successful) scan attempt only.
 *  - BarcodeScanTotalTimeMs                   total time spent on the barcode
 *                                             scan op for the container, summed
 *                                             across every attempt (invalid /
 *                                             duplicate / unknown + final), from
 *                                             scan-screen ready until success.
 *  - BarcodeScanAttemptCount                  number of attempts it took to land
 *                                             a valid barcode (incl. dup/unknown).
 *
 * ============================================
 * QUERY IN CLOUDWATCH LOGS INSIGHTS (per-run detail):
 * ============================================
 *   fields katalLogger.message
 *   | filter katalLogger.message like /\[BarcodeScanTiming\]/
 *   | sort @timestamp desc
 *
 * Each log line carries `inspectionId` (log only, not a metric dimension) so a
 * single run can be traced end-to-end. To scope to one inspection:
 *   | filter katalLogger.message like /"inspectionId":"<id>"/
 */

import { cloudWatchLogger } from "./CloudWatchLoggerService";

export type BarcodeEntryMethod = "camera" | "manual";

/**
 * Outcome of a scan attempt:
 *  - "scanned":   a valid, new barcode was accepted.
 *  - "duplicate": the barcode was already scanned this session.
 *  - "unknown":   the barcode prefix isn't a recognized container type.
 * duplicate / unknown attempts still took the user time, so we log them for
 * visibility, but only "scanned" attempts feed the aggregate metric.
 */
export type BarcodeScanOutcome = "scanned" | "duplicate" | "unknown";

export interface BarcodeScanParams {
  /** How the barcode was obtained: scanned by camera or typed manually. */
  entryMethod: BarcodeEntryMethod;
  /** Outcome of the attempt (see BarcodeScanOutcome). */
  outcome: BarcodeScanOutcome;
  /**
   * Site code the inspection is running at (e.g. "BFI4"). Log only, not a
   * metric dimension.
   */
  siteCode?: string | null;
  /**
   * SafetyGenie inspection id this scan belongs to (log only, not a metric
   * dimension — unbounded cardinality). Lets a run be correlated end-to-end
   * with the capture-timing logs and backend/service logs for the same
   * inspection.
   */
  inspectionId?: string | null;
  /** The barcode value, for per-run drill-down (log only, not a metric dimension). */
  containerId?: string | null;
  /** 1-based index of the container in the current inspection run, if known. */
  containerIndex?: number | null;
}

interface BarcodeScanMark {
  startTime: number;
}

class BarcodeScanTimingLogger {
  // Per-attempt clock. Consumed (nulled) on every recordScan and re-armed by
  // start(), so repeated scanner fires for the same barcode don't double-count.
  private currentMark: BarcodeScanMark | null = null;
  // Container-level clock. Set on the FIRST start() for a container and kept
  // across duplicate / unknown retries so we can report the total time spent on
  // the barcode-scan op. Cleared only on a successful scan or cancel().
  private containerStartTime: number | null = null;
  // Number of attempts (incl. dup / unknown) made for the current container.
  private attemptCount = 0;

  /**
   * Mark the start of a barcode-scan attempt. Called when the scan screen
   * becomes ready (component mounted / scanner active) and again after a
   * duplicate / unknown rescan. The first call for a container also starts the
   * container-level clock; subsequent retries reuse it.
   */
  start(): void {
    this.currentMark = { startTime: Date.now() };
    // Begin container-level timing on the first attempt only; retries reuse it.
    if (this.containerStartTime === null) {
      this.containerStartTime = this.currentMark.startTime;
    }
  }

  /** Whether a scan is currently being timed. */
  isActive(): boolean {
    return this.currentMark !== null;
  }

  /**
   * Record a barcode-scan attempt: emit a timing log line for every attempt,
   * and (only for a successful "scanned" outcome) the aggregate metrics. The
   * per-attempt scan time is computed from the last start() mark; the total
   * time is computed from the container-level clock and therefore spans every
   * attempt (invalid / duplicate / unknown + final), including the time the
   * user spent on the alert modals in between. No-op if there is no active
   * per-attempt mark, so repeated scanner fires for the same barcode don't
   * double-count — call start() again to time a fresh attempt.
   */
  recordScan(params: BarcodeScanParams): void {
    const mark = this.currentMark;
    if (!mark) {
      return;
    }
    const now = Date.now();
    const scanMs = now - mark.startTime;
    this.currentMark = null;
    this.attemptCount += 1;

    const totalMs = this.containerStartTime != null ? now - this.containerStartTime : scanMs;

    const payload = {
      metric: "BarcodeScanTiming",
      entryMethod: params.entryMethod,
      outcome: params.outcome,
      siteCode: params.siteCode ?? null,
      inspectionId: params.inspectionId ?? null,
      containerId: params.containerId ?? null,
      containerIndex: params.containerIndex ?? null,
      attemptNumber: this.attemptCount, // 1-based attempt index within this container
      scanMs, // this attempt only
      totalMsSoFar: totalMs, // cumulative for the container so far
    };

    // Per-run, greppable log line for every attempt (camera or manual,
    // regardless of outcome) so manual entries and dup/unknown attempts are
    // always visible for debugging.
    cloudWatchLogger.info(`[BarcodeScanTiming] ${JSON.stringify(payload)}`);

    // Only a successful scan feeds the aggregate metrics. We publish:
    //  - BarcodeScanTimeMs: the final attempt only (kept for back-compat).
    //  - BarcodeScanTotalTimeMs: total across all attempts for the container.
    //  - BarcodeScanAttemptCount: how many attempts it took.
    // duplicate / unknown attempts only measure "time until we rejected it", so
    // they stay in the log line (for drill-down) and feed the total via the
    // container clock, but are not emitted as standalone metrics. EMF document
    // so CloudWatch auto-publishes the metrics. Only the bounded entryMethod is
    // a dimension; containerId stays in the log line to keep cardinality low
    // (same rule as captureTimingLogger).
    if (params.outcome === "scanned") {
      const attempts = this.attemptCount;
      // Terminal outcome: end container-level timing and reset attempt counter.
      this.containerStartTime = null;
      this.attemptCount = 0;

      cloudWatchLogger.emitMetric({
        _aws: {
          Timestamp: Date.now(),
          CloudWatchMetrics: [
            {
              Namespace: "SafetyGenie/ClientCapture",
              Dimensions: [["entryMethod"]],
              Metrics: [
                { Name: "BarcodeScanTimeMs", Unit: "Milliseconds" },
                { Name: "BarcodeScanTotalTimeMs", Unit: "Milliseconds" },
                { Name: "BarcodeScanAttemptCount", Unit: "Count" },
              ],
            },
          ],
        },
        entryMethod: params.entryMethod,
        BarcodeScanTimeMs: scanMs,
        BarcodeScanTotalTimeMs: totalMs,
        BarcodeScanAttemptCount: attempts,
      });
    }
  }

  /**
   * Discard all timing state (e.g. scan screen abandoned / unmounted). Clears
   * both the per-attempt mark and the container-level clock.
   */
  cancel(): void {
    this.currentMark = null;
    this.containerStartTime = null;
    this.attemptCount = 0;
  }
}

export const barcodeScanTimingLogger = new BarcodeScanTimingLogger();

export default barcodeScanTimingLogger;
